<template>
  <div class="modal" :class="{ 'is-active': is_open }">
    <div class="modal-background" @click="close"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ $t("request details") }}</p>
        <button class="delete" @click="close"></button>
      </header>
      <section class="modal-card-body">
        <div v-if="is_loading" class="has-text-centered">
          <i class="fa fa-spinner fa-spin fa-2x"></i>
          <p class="mt-3">{{ $t("loading") }}...</p>
        </div>

        <div v-else-if="request">
          <!-- Customer Information -->
          <div class="box">
            <h4 class="title is-5">{{ $t("customer information") }}</h4>

            <div class="columns is-multiline">
              <div class="column is-6">
                <div class="field">
                  <label class="label">{{ $t("customer name") }}</label>
                  <div class="control">
                    <input
                      class="input"
                      :value="request.customer_name"
                      readonly
                    />
                  </div>
                </div>
              </div>

              <div class="column is-6">
                <div class="field">
                  <label class="label">{{ $t("pagare number") }}</label>
                  <div class="control">
                    <input
                      class="input"
                      :value="request.pagare_number"
                      readonly
                    />
                  </div>
                </div>
              </div>

              <div class="column is-6">
                <div class="field">
                  <label class="label">{{ $t("nit") }}</label>
                  <div class="control">
                    <input
                      class="input"
                      :value="request.nit || $t('not provided')"
                      readonly
                    />
                  </div>
                </div>
              </div>

              <div class="column is-6">
                <div class="field">
                  <label class="label">{{ $t("dpi") }}</label>
                  <div class="control">
                    <input
                      class="input"
                      :value="request.dpi || $t('not provided')"
                      readonly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Request Status -->
          <div class="box">
            <h4 class="title is-5">{{ $t("request status") }}</h4>

            <div class="columns">
              <div class="column is-6">
                <div class="field">
                  <label class="label">{{ $t("current status") }}</label>
                  <div class="control">
                    <span
                      class="tag is-large"
                      :class="getStatusClass(request.status)"
                    >
                      {{ $t(request.status || "pending") }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="column is-6">
                <div class="field">
                  <label class="label">{{ $t("request date") }}</label>
                  <div class="control">
                    <input
                      class="input"
                      :value="formatDate(request.create_date)"
                      readonly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional Notes -->
          <div class="box" v-if="request.settings && request.settings.notes">
            <h4 class="title is-5">{{ $t("additional notes") }}</h4>
            <div class="content">
              <p>{{ request.settings.notes }}</p>
            </div>
          </div>

          <!-- Related Evaluations -->
          <div class="box" v-if="evaluations && evaluations.length > 0">
            <h4 class="title is-5">{{ $t("related evaluations") }}</h4>

            <div class="table-container">
              <table class="table is-fullwidth is-striped">
                <thead>
                  <tr>
                    <th>{{ $t("evaluation date") }}</th>
                    <th>{{ $t("result") }}</th>
                    <th>{{ $t("score") }}</th>
                    <th>{{ $t("status") }}</th>
                    <th>{{ $t("actions") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="evaluation in evaluations" :key="evaluation.uuid">
                    <td>{{ formatDate(evaluation.create_date) }}</td>
                    <td>
                      <span
                        class="tag"
                        :class="getResultClass(evaluation.analysis_result)"
                      >
                        {{ $t(evaluation.analysis_result || "pending review") }}
                      </span>
                    </td>
                    <td>{{ evaluation.score || $t("not scored") }}</td>
                    <td>
                      <span
                        class="tag"
                        :class="getEvaluationStatusClass(evaluation.status)"
                      >
                        {{ $t(evaluation.status || "draft") }}
                      </span>
                    </td>
                    <td>
                      <button
                        class="button is-small is-info"
                        @click="viewEvaluation(evaluation.uuid)"
                      >
                        <span class="icon">
                          <i class="fa fa-eye"></i>
                        </span>
                        <span>{{ $t("view") }}</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- No Evaluations Message -->
          <div
            class="box has-text-centered"
            v-else-if="!is_loading_evaluations"
          >
            <i class="fa fa-clipboard-list fa-3x has-text-grey-light"></i>
            <p class="mt-3 has-text-grey">
              {{ $t("no evaluations found for this request") }}
            </p>
            <button class="button is-primary mt-3" @click="createEvaluation">
              <span class="icon">
                <i class="fa fa-plus"></i>
              </span>
              <span>{{ $t("create evaluation") }}</span>
            </button>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot">
        <button class="button is-primary" @click="editRequest" v-if="request">
          <span class="icon">
            <i class="fa fa-edit"></i>
          </span>
          <span>{{ $t("edit request") }}</span>
        </button>
        <button class="button" @click="close">{{ $t("close") }}</button>
      </footer>
    </div>
  </div>
</template>

<script>
import { get, error_message } from "@/utils";
import moment from "moment";

export default {
  name: "CreditAnalysisRequestDetailModal",
  data() {
    return {
      is_open: false,
      is_loading: false,
      is_loading_evaluations: false,
      uuid: null,
      request: null,
      evaluations: [],
    };
  },
  methods: {
    open(uuid) {
      this.uuid = uuid;
      this.is_open = true;
      this.loadRequest();
      this.loadEvaluations();
    },
    close() {
      this.is_open = false;
      this.request = null;
      this.evaluations = [];
    },
    loadRequest() {
      this.is_loading = true;

      get("/credit-analysis/request", { uuid: this.uuid })
        .then((response) => {
          this.request = response;
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => {
          this.is_loading = false;
        });
    },
    loadEvaluations() {
      this.is_loading_evaluations = true;

      // Simulate loading evaluations - replace with actual API call
      setTimeout(() => {
        // This should be replaced with actual API call to get evaluations by request_uuid
        this.evaluations = [];
        this.is_loading_evaluations = false;
      }, 500);
    },
    editRequest() {
      this.$emit("edit", this.uuid);
      this.close();
    },
    createEvaluation() {
      this.$router.push({
        path: "/credit-analysis/evaluations",
        query: { request_uuid: this.uuid },
      });
      this.close();
    },
    viewEvaluation(evaluationUuid) {
      this.$router.push({
        path: "/credit-analysis/evaluations",
        query: { view: evaluationUuid },
      });
      this.close();
    },
    formatDate(date) {
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    getStatusClass(status) {
      const classes = {
        pending: "is-warning",
        in_progress: "is-info",
        completed: "is-success",
      };
      return classes[status] || "is-light";
    },
    getResultClass(result) {
      const classes = {
        approved: "is-success",
        rejected: "is-danger",
        pending_review: "is-warning",
      };
      return classes[result] || "is-light";
    },
    getEvaluationStatusClass(status) {
      const classes = {
        draft: "is-light",
        completed: "is-success",
        archived: "is-dark",
      };
      return classes[status] || "is-light";
    },
  },
};
</script>

<style scoped>
.modal-card {
  width: 95%;
  max-width: 800px;
  max-height: 90vh;
}

.modal-card-body {
  max-height: 70vh;
  overflow-y: auto;
}

.box {
  margin-bottom: 1.5rem;
}

.table-container {
  overflow-x: auto;
}

.tag.is-large {
  font-size: 1rem;
  padding: 0.5rem 1rem;
}
</style>
